<script setup>
import {useVuelidate} from '@vuelidate/core'
import {required, email, sameAs} from '@vuelidate/validators'
import {reactive} from '../../../node_modules/vue/dist/vue.esm-bundler';
import {ref, onMounted} from "vue";

const props = defineProps({
  subjects: Object,
  alternateText: String,
  topic: String
})

const state = reactive({
  emailSent: false,
  alternateSubject: '',
  isExistingCustomer: null,
  isProblemSolved: null,
  isFileAttached: false,
  isPrivacyChecked: false,
  accordions: {},
  form: {
    subject: '',
    salutation: '',
    firstname: '',
    lastname: '',
    email: '',
    message: '',
    filename: ''
  }
});

const contactForm = ref(null);

const rules = {
  isExistingCustomer: {required, $autoDirty: true},
  isPrivacyChecked: {required, sameAs: sameAs(true), $autoDirty: true},
  form: {
    subject: {required, $autoDirty: true},
    salutation: {required, $autoDirty: true},
    firstname: {required, $autoDirty: true},
    lastname: {required, $autoDirty: true},
    email: {required, email, $autoDirty: true},
    message: {required, $autoDirty: true},
  }
};

const v$ = useVuelidate(rules, state)

onMounted(() => {
  for (const [index, subject] of Object.entries(props.subjects)) {
    const faqs = subject.faqs;

    if (faqs) {
      for (const [key, faq] of Object.entries(faqs)) {

        state.accordions[faq.id] = {active: false};
      }
    } else {
      state.alternateSubject = subject.label;
    }
  }
});

const toggleAccordionItem = (id) => {
  state.accordions[id].active = !state.accordions[id].active;

}

const handleFile = (event) => {
  if (event.target.files) {
    const file = event.target.files[0];
    state.form.filename = file.name;
  }
}

const submit = () => {
  v$.value.$touch();

  const formData = new FormData(contactForm.value);

  if (v$.value.$errors.length === 0) {
    fetch('/form-submit', {
      body: formData,
      method: 'POST'
    }).then(response => {
      state.emailSent = true;
    }).catch(error => {
      state.emailSent = true;
    });
  }
}

</script>

<template>
  <div class="columns ">
    <div class="column is-8 text-flow">
      <h2 class="heading-2">Ihr Anliegen</h2>
      <h3 class="heading-3">Ihre Nachricht an uns zum Thema {{ topic }}</h3>
    </div>
  </div>
  <form action="" class="text-flow" ref="contactForm" v-if="!state.emailSent">
    <div class="columns is-multiline">
      <div class="column is-8">
        <div class="field is-horizontal">
          <div class="field-label">
            <label class="label">Sind Sie bereits Kunde?</label>
          </div>
          <div class="field-body">
            <div class="control">
              <label class="radio">
                <input type="radio" name="existing-customer" :value="true" v-model="state.isExistingCustomer">
                Ja
              </label>
              <label class="radio">
                <input type="radio" name="existing-customer" :value="false" v-model="state.isExistingCustomer">
                Nein
              </label>
            </div>
          </div>
        </div>

        <div class="field is-horizontal" v-if="state.isExistingCustomer !== null">
          <div class="field-label is-normal">
            <label for="form.subject" class="label">Betreff Ihrer Anfrage</label>
          </div>
          <div class="field-body">
            <div class="control">
              <div class="select">
                <select id="form.subject" name="subject" v-model="state.form.subject">
                  <option :value="key" v-for="(value, key) in subjects" :key="value.label">{{
                      value.label
                    }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column is-12">
        <div v-for="(subject, key) in subjects">
          <div v-if="key !== '' && state.form.subject === key && subject.faqs">
            <h4 class="heading-4">{{ subject.label }}</h4>
            <div class="accordion">
              <div class="accordion-item"
                   v-for="faq in subject.faqs">
                <div class="accordion-title" :class="{ 'is-active': state.accordions[faq.id].active }"
                     @click="toggleAccordionItem(faq.id)"><h4 class="heading-4">{{ faq.question }}</h4>
                </div>
                <div class="accordion-content content text-flow" v-html="faq.answer"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="state.form.subject === state.alternateSubject">
          <div v-html="alternateText"></div>
        </div>
      </div>

      <div class="column is-8">
        <div class="field is-horizontal" v-if="state.form.subject !== ''">
          <div class="field-label">
            <label class="label">Wurde Ihre Anfrage beantwortet?</label>
          </div>
          <div class="field-body">
            <div class="control">
              <label class="radio">
                <input type="radio" name="problem-solved" :value="true" v-model="state.isProblemSolved">
                Ja
              </label>
              <label class="radio">
                <input type="radio" name="problem-solved" :value="false" v-model="state.isProblemSolved">
                Nein
              </label>
            </div>
          </div>
        </div>

        <div v-if="state.isProblemSolved === false && state.isExistingCustomer === false" class="flow">
          <div class="field is-horizontal">
            <div class="field-body">
              <div class="control">
                <label class="radio">
                  <input type="radio" name="salutation" value="herr" v-model="state.form.salutation" @blur="v$.form.salutation.$touch">
                  Herr
                </label>
                <label class="radio">
                  <input type="radio" name="salutation" value="frau" v-model="state.form.salutation" @blur="v$.form.salutation.$touch">
                  Frau
                </label>
              </div>
            </div>
          </div>
          <div role="alert" aria-live="assertive">
            <p v-if="v$.form.salutation.$errors.length > 0" class="help is-danger">Bitte überprüfen Sie ihre Eingabe.</p>
          </div>

          <div class="field has-floating-label">
            <label for="form.firstname" class="label">Vorname</label>
            <div class="control">
              <input id="form.firstname" class="input" :class="{ 'is-danger' : v$.form.firstname.$errors.length > 0 }" type="text" name="firstname" placeholder="Vorname *"
                     v-model="state.form.firstname" @blur="v$.form.firstname.$touch">
            </div>
            <div role="alert" aria-live="assertive">
                <p v-if="v$.form.firstname.$errors.length > 0" class="help is-danger">Bitte überprüfen Sie ihre Eingabe.</p>
            </div>
          </div>

          <div class="field has-floating-label">
            <label for="form.lastname" class="label">Nachname *</label>
            <div class="control">
              <input id="form.lastname" class="input" :class="{ 'is-danger' : v$.form.lastname.$errors.length > 0 }" type="text" name="lastname" placeholder="Nachname *"
                     v-model="state.form.lastname"  @blur="v$.form.lastname.$touch">
            </div>
            <div role="alert" aria-live="assertive">
                <p v-if="v$.form.lastname.$errors.length > 0" class="help is-danger">Bitte überprüfen Sie ihre Eingabe.</p>
            </div>
          </div>

          <div class="field has-floating-label">
            <label for="form.email" class="label">E-Mail *</label>
            <div class="control">
              <input id="form.email" class="input" :class="{ 'is-danger' : v$.form.email.$errors.length > 0 }" type="email" name="email" placeholder="E-Mail *"
                     v-model="state.form.email" @blur="v$.form.email.$touch">
            </div>
            <div role="alert" aria-live="assertive">
                <p v-if="v$.form.email.$errors.length > 0" class="help is-danger">Bitte überprüfen Sie ihre Eingabe.</p>
            </div>
          </div>

          <div class="field has-floating-label">
            <label for="form.message" class="label">Ihre Anfrage *</label>
            <div class="control">
                  <textarea id="form.message" name="message" rows="4" class="input"
                            v-model="state.form.message" :class="{ 'is-danger' : v$.form.message.$errors.length > 0 }"  @blur="v$.form.message.$touch"></textarea>
            <div role="alert" aria-live="assertive">
              <p v-if="v$.form.message.$errors.length > 0" class="help is-danger">Bitte überprüfen Sie ihre Eingabe.</p>
            </div>
            </div>
          </div>

          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" name="attach-file" v-model="state.isFileAttached">
                Datei anhängen
              </label>
            </div>
          </div>

          <div v-if="state.isFileAttached">
            <div class="field">
              <label for="form.message" class="label">Anhang hochladen</label>
              <div class="control">
                <div class="file has-name is-fullwidth">
                  <label class="file-label">
                    <input class="file-input" type="file" name="attachment" @change="handleFile">
                    <span class="file-cta"><span class="file-icon"><i class="fas fa-upload"></i></span><span
                        class="file-label">Datei auswählen</span></span>
                    <span class="file-name">{{ state.form.filename }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="box background-grey-2">
              <p><i class="far fa-lightbulb"></i> <strong>Hinweis</strong>: Die Übertragung der Daten an uns erfolgt in
                verschlüsselter Form.
                Dennoch möchten wir Sie bitten, uns keine zusätzlichen persönlichen Daten mit Ihrer Anfrage zu
                übermitteln.</p>
            </div>
          </div>

          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" name="attach-file" v-model="state.isPrivacyChecked">
                Ich habe die <a target="_blank" href="https://www.opel-direktbank.de/datenschutzinformationen">Datenschutzinformation</a> zur Kenntnis genommen.
              </label>
            </div>
            <div role="alert" aria-live="assertive">
                <p v-if="v$.isPrivacyChecked.$errors.length > 0" class="help is-danger">Bitte überprüfen Sie ihre Eingabe.</p>
            </div>
          </div>
        </div>

        <div class="buttons is-right" v-if="state.isExistingCustomer === false && state.isProblemSolved === false">
          <a @click.prevent="submit" @keydown.enter.prevent="submit" @keydown.space.prevent="submit" class="button is-primary js-aria-send-button" tabindex="0">Absenden</a>
        </div>

        <div v-else-if="state.isExistingCustomer === true && state.isProblemSolved === false">
          <div class="box background-grey-2">
            <p><i class="far fa-lightbulb"></i> Bitte melden Sie sich im Opel Bank eBanking an und wählen Sie den
              Menüpunkt "Nachrichten". So kann Ihre Anfrage direkt dem richtigen Ansprechpartner zugeordnet und
              schneller beantwortet werden. <br>
              <strong><a href="https://ebanking.opel-direktbank.de/eBankingClient/pages/login/login.jsf">Zur
                Anmeldung</a></strong></p>
          </div>
        </div>
      </div>
    </div>
  </form>
  <div v-else>
    <div class="columns">
      <div class="column text-flow">
        <h2 class="heading-2">Vielen Dank</h2>
        <p>Ihre Nachricht ist bei uns eingegangen und wird von uns schnellstmöglich bearbeitet. Üblicherweise erhalten Sie innerhalb von zwei Werktagen Rückmeldung. Bitte beachten Sie, dass Anfragen, die uns am Wochenende oder an Feiertagen erreichen, eine entsprechend längere Bearbeitungszeit aufweisen.</p>
        <p><strong>Hinweis:</strong> In der aktuellen Lage kann es immer noch zu einer erhöhten Anzahl von Anfragen im Kundenservice kommen. Daher bitten wir um Verständnis, falls unsere Rückmeldung Sie nicht innerhalb der üblichen Fristen erreicht.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
